// components/ChoreRow.tsx
import React, { useEffect, useRef, useState } from 'react';
import { ArrowPathIcon, ClockIcon, PencilSquareIcon, TrashIcon, UserIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ChoreStatus } from '../domain';
import { useChoreRow } from '../hooks/useChoreRow';
import ChoreFormDialog from './ChoreFormDialog';
import CompletionRing from './CompletionRing';
import {
  ActionMenu, Avatar, Badge, Button, Chip, ConfirmDialog, StatusDot, Dialog, DialogBody, DialogFooter, Field, Input, Tone, cx, focusRing,
} from './ui';

/** Long enough to see the tick land before the chore moves on to its next due date. */
const COMPLETE_DELAY_MS = 450;

const BADGE_TONE: Record<ChoreStatus, Tone> = {
  [ChoreStatus.Overdue]: 'overdue',
  [ChoreStatus.DueToday]: 'today',
  // Upcoming stays quiet: the ring carries the accent, the badge only the date.
  [ChoreStatus.Upcoming]: 'neutral',
};

/** The due date as it reads under the name on a phone, in place of the badge. */
const DUE_TEXT: Record<ChoreStatus, string> = {
  [ChoreStatus.Overdue]: 'text-status-overdue-text dark:text-red-400',
  [ChoreStatus.DueToday]: 'text-status-today-text dark:text-amber-400',
  [ChoreStatus.Upcoming]: 'text-gray-600 dark:text-gray-300',
};

function renderWithLinks(text: string) {
  return text.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="break-all text-indigo-600 underline hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300">{part}</a>
      : part
  );
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export const ChoreRow: React.FC<{ householdId: string; choreId: string }> = ({ householdId, choreId }) => {
  const { state, actions } = useChoreRow(householdId, choreId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [completing, setCompleting] = useState(false);
  const completeTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(completeTimer.current), []);

  if (!state || !actions) {
    return <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">Chore not found</div>;
  }

  const {
    name, description, labels, status, dueText, nextDueText, ringProgress, householdName, showHousehold,
    assignees, scheduleDescription, lastCompletedText, user, mutating, editFormData, editError,
    showEditModal, showDeleteConfirm, householdMembers, showBackdatePicker, backdateValue, choreCreatedAt, canEdit,
  } = state;

  const canAct = !!user && !mutating;
  const primary = assignees[0];
  // The avatar shows only the primary, so say when you're on the chore with them.
  const sharedWithYou = !!user && primary?.id !== user.uid && assignees.some(a => a.id === user.uid);
  const detailsId = `chore-${choreId}-details`;

  // The tick and strike-through show first; the chore is marked done once they've landed, and
  // the row then moves on to its next due date with the undo toast up.
  const complete = () => {
    if (!canAct || completing) return;
    setCompleting(true);
    setIsExpanded(false);
    completeTimer.current = setTimeout(() => {
      Promise.resolve(actions.handleMarkDone()).finally(() => setCompleting(false));
    }, prefersReducedMotion() ? 0 : COMPLETE_DELAY_MS);
  };

  return (
    <div className={cx('px-4 py-3 transition-colors', isExpanded ? 'bg-gray-50/70 dark:bg-gray-800/40' : 'hover:bg-gray-50/70 dark:hover:bg-gray-800/40')}>
      <div className="flex items-center gap-3">
        <CompletionRing
          status={status}
          progress={ringProgress}
          done={completing}
          label={`Mark “${name}” done`}
          disabled={!canAct}
          onClick={complete}
        />

        <button
          type="button"
          onClick={() => setIsExpanded(e => !e)}
          aria-expanded={isExpanded}
          aria-controls={detailsId}
          className={cx('min-w-0 flex-1 rounded-md text-left', focusRing)}
        >
          <span
            className={cx(
              // Up to two lines: on a phone a long name would otherwise be cut off after a word or two.
              'line-clamp-2 break-words text-[15px] font-semibold text-gray-900 transition-colors duration-200 dark:text-gray-100',
              completing && 'text-gray-400 line-through dark:text-gray-500',
            )}
          >
            {name}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            {/* On a phone the due date leads this line, leaving the name the width of the row. */}
            <span className={cx('inline-flex items-center gap-1.5 font-semibold sm:hidden', DUE_TEXT[status])}>
              <StatusDot tone={BADGE_TONE[status]} />
              {dueText}
            </span>
            <span aria-hidden="true" className="text-gray-300 dark:text-gray-600 sm:hidden">·</span>
            {showHousehold && (
              <>
                <span>{householdName}</span>
                <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">·</span>
              </>
            )}
            <span className="inline-flex items-center gap-1">
              <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {scheduleDescription}
            </span>
            {sharedWithYou && (
              <>
                <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">·</span>
                <span className="inline-flex items-center gap-1">
                  <UserIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Shared with you
                </span>
              </>
            )}
            {labels.length > 0 && <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">·</span>}
            {labels.map(label => <Chip key={label} size="sm">{label}</Chip>)}
          </span>
        </button>

        {/* Left out on a phone: the due date is under the name, and the actions are in the details. */}
        <div className="hidden flex-none items-center gap-2 sm:flex">
          <Badge tone={BADGE_TONE[status]} dot>{dueText}</Badge>
          {primary && <Avatar name={primary.name} id={primary.id} />}
          <ActionMenu
            label={`More actions for ${name}`}
            items={[
              { label: 'Done on an earlier date…', icon: ClockIcon, onClick: actions.openBackdatePicker, disabled: !canAct },
              ...(canEdit ? [
                { label: 'Edit', icon: PencilSquareIcon, onClick: actions.openEditModal },
                'separator' as const,
                { label: 'Delete…', icon: TrashIcon, onClick: () => actions.setShowDeleteConfirm(true), danger: true, disabled: !canAct },
              ] : []),
            ]}
          />
        </div>
      </div>

      {isExpanded && (
        <div id={detailsId} className="mt-3 space-y-4 border-t border-dashed border-gray-200 pt-3 dark:border-gray-700 sm:ml-12">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
            <div className="col-span-2 sm:col-span-1">
              <dt className="text-xs text-gray-500 dark:text-gray-400">Assigned to</dt>
              <dd className="mt-1 flex flex-col gap-1">
                {assignees.length === 0 && <span className="text-gray-500 dark:text-gray-400">No one</span>}
                {assignees.map(a => (
                  <span key={a.id} className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-100">
                    <Avatar name={a.name} id={a.id} size="xs" />
                    <span className="truncate">{a.name}</span>
                    {a.isPrimary && assignees.length > 1 && <Badge tone="brand" size="sm">Primary</Badge>}
                  </span>
                ))}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">Due</dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{nextDueText}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">Repeats</dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{scheduleDescription}</dd>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <dt className="text-xs text-gray-500 dark:text-gray-400">Last done</dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{lastCompletedText ?? 'Never'}</dd>
            </div>
            {description && (
              <div className="col-span-2 sm:col-span-4">
                <dt className="text-xs text-gray-500 dark:text-gray-400">Notes</dt>
                <dd className="mt-1 whitespace-pre-line text-gray-700 dark:text-gray-300">{renderWithLinks(description)}</dd>
              </div>
            )}
          </dl>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" size="sm" icon={CheckIcon} onClick={complete} disabled={!canAct}>
              Mark done
            </Button>
            {canEdit && (
              <Button size="sm" icon={PencilSquareIcon} onClick={actions.openEditModal}>
                Edit
              </Button>
            )}
            {/* On a phone there's no ⋯ menu, so the rest of its actions are here. */}
            <span className="contents sm:hidden">
              <Button size="sm" variant="ghost" icon={ClockIcon} onClick={actions.openBackdatePicker} disabled={!canAct}>
                Earlier date…
              </Button>
              {canEdit && (
                <Button size="sm" variant="danger-ghost" icon={TrashIcon} onClick={() => actions.setShowDeleteConfirm(true)} disabled={!canAct}>
                  Delete…
                </Button>
              )}
            </span>
          </div>
        </div>
      )}

      <Dialog
        open={showBackdatePicker}
        onClose={() => actions.setShowBackdatePicker(false)}
        title="When was it done?"
        description={`“${name}” will be marked done on that day, and its next due date counted from it.`}
        size="sm"
      >
        <form onSubmit={actions.handleBackdateSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogBody>
            <Field label="Date" htmlFor={`${detailsId}-backdate`}>
              <Input
                id={`${detailsId}-backdate`}
                type="date"
                value={backdateValue}
                min={choreCreatedAt.split('T')[0]}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => actions.setBackdateValue(e.target.value)}
                required
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => actions.setShowBackdatePicker(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={!canAct || !backdateValue}>Mark done</Button>
          </DialogFooter>
        </form>
      </Dialog>

      <ChoreFormDialog
        mode="edit"
        open={showEditModal}
        onClose={() => actions.setShowEditModal(false)}
        onSubmit={actions.handleEditSubmit}
        values={editFormData}
        onChange={actions.updateField}
        members={householdMembers}
        toggleAssignment={actions.toggleAssignment}
        toggleResponsible={actions.toggleResponsible}
        householdName={householdName}
        loading={mutating}
        error={editError ?? undefined}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => actions.setShowDeleteConfirm(false)}
        onConfirm={actions.handleDelete}
        title="Delete this chore?"
        message={<>“{name}” and its history will be deleted. This can't be undone.</>}
        confirmLabel="Delete chore"
        tone="danger"
        loading={mutating}
      />
    </div>
  );
};

export default ChoreRow;
